import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent, ReactElement, ReactNode, RefObject } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '../../lib/cn'
import { useOnClickOutside } from '../../hooks/useOnClickOutside'
import { computePosition } from './floating'
import type { Align, Placement } from './floating'

export interface PopoverProps {
  open: boolean
  onClose: () => void
  /** The element the panel hangs off. Usually the trigger button. */
  anchorRef: RefObject<HTMLElement | null>
  placement?: Placement
  align?: Align
  offset?: number
  surface?: 'paper' | 'chrome'
  /**
   * `menu` when the panel is a list of `MenuItem`s: it becomes a `role="menu"`, focus lands on
   * the first item when it opens, and Up/Down/Home/End walk the items. Anything richer than a
   * list of commands — a search field, a date grid — stays a `dialog`.
   */
  role?: 'dialog' | 'menu'
  /** Names the panel for assistive tech. */
  label: string
  className?: string
  children: ReactNode
}

/**
 * A floating panel anchored to a trigger: menus, filter sheets, the date picker.
 * Closes on outside press, on Escape, and when the anchor scrolls out of view. Escape puts
 * focus back on the trigger, because that is where the user left it.
 */
export const Popover = ({
  open,
  onClose,
  anchorRef,
  placement = 'bottom',
  align = 'start',
  offset = 6,
  surface = 'paper',
  role = 'dialog',
  label,
  className,
  children,
}: PopoverProps): ReactElement | null => {
  const panelRef = useRef<HTMLDivElement | null>(null)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)

  const outsideRefs = useMemo(() => [panelRef, anchorRef], [anchorRef])
  const handleOutside = useCallback(() => {
    onClose()
  }, [onClose])
  useOnClickOutside(outsideRefs, handleOutside, open)

  useEffect(() => {
    if (!open) {
      setPosition(null)
      return
    }
    const anchor = anchorRef.current
    const panel = panelRef.current
    if (!anchor || !panel) return

    const update = (): void => {
      const next = computePosition(
        anchor.getBoundingClientRect(),
        { width: panel.offsetWidth, height: panel.offsetHeight },
        placement,
        align,
        offset,
      )
      setPosition({ top: next.top, left: next.left })
    }

    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)

    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [open, anchorRef, placement, align, offset])

  /* Escape lives on its own so it still works when the anchor has not resolved. */
  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      anchorRef.current?.focus()
      onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, anchorRef, onClose])

  const menuItems = useCallback((): HTMLElement[] => {
    const panel = panelRef.current
    if (!panel) return []
    return Array.from(panel.querySelectorAll<HTMLElement>('[role="menuitem"]'))
  }, [])

  /**
   * What takes focus when the panel opens: the first command for a menu, the first focusable
   * thing for anything richer (a search field, a date grid) — a keyboard user who opened this
   * panel is not left outside it with nowhere to Tab from.
   */
  const focusTarget = useCallback((): HTMLElement | null => {
    const panel = panelRef.current
    if (!panel) return null
    if (role === 'menu') return menuItems()[0] ?? null
    return panel.querySelector<HTMLElement>(
      'input, textarea, select, button:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])',
    )
  }, [role, menuItems])

  /**
   * It has to wait for the first position, though — the panel is `visibility: hidden` until then
   * to stop it flashing at 0,0, and a hidden element cannot be focused (this is also why a plain
   * `autoFocus` prop on a child doesn't work here: it fires at mount, while still hidden). Once
   * per opening.
   */
  const tookFocus = useRef(false)
  useEffect(() => {
    if (!open) {
      tookFocus.current = false
      return
    }
    if (position === null || tookFocus.current) return
    tookFocus.current = true
    focusTarget()?.focus()
  }, [open, position, focusTarget])

  const onPanelKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (role !== 'menu') return
    const items = menuItems()
    if (items.length === 0) return
    const current = items.findIndex((item) => item === document.activeElement)

    const next =
      event.key === 'ArrowDown'
        ? (current + 1) % items.length
        : event.key === 'ArrowUp'
          ? (current <= 0 ? items.length : current) - 1
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? items.length - 1
              : null

    if (next === null) return
    event.preventDefault()
    items[next]?.focus()
  }

  if (!open) return null

  return createPortal(
    <div
      ref={panelRef}
      role={role}
      aria-label={label}
      aria-orientation={role === 'menu' ? 'vertical' : undefined}
      onKeyDown={onPanelKeyDown}
      style={{
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        visibility: position ? 'visible' : 'hidden',
      }}
      className={cn(
        // Above Modal/Drawer (z-60): a popover anchored to a trigger inside a dialog — the
        // FontPicker in the image editor is the first case — must still render on top of it.
        'anim-rise fixed z-70 min-w-48 rounded-md border shadow-float',
        surface === 'chrome'
          ? 'border-chrome-600 bg-chrome-700 text-chrome-ink'
          : 'border-hair bg-surface text-ink',
        className,
      )}
    >
      {children}
    </div>,
    document.body,
  )
}

export interface MenuItemProps {
  onSelect: () => void
  icon?: ReactNode
  /** A keyboard hint rendered in mono on the right. */
  shortcut?: string
  danger?: boolean
  /**
   * Unavailable, not absent. The row stays in the menu and stays focusable — `aria-disabled`
   * rather than the `disabled` attribute — so the keyboard can reach it and hear why it is off.
   * A removed command reads as a command that does not exist.
   */
  disabled?: boolean
  /** Id of the element saying why this row is unavailable. Pair it with `MenuNote`. */
  describedBy?: string
  active?: boolean
  surface?: 'paper' | 'chrome'
  /** A stable hook for a guided tour to point at — the same `data-tour="nav-…"` convention
   *  `NavRail.tsx` derives from its routes, spelled out here since a menu row has no route of
   *  its own to derive it from. */
  'data-tour'?: string
  children: ReactNode
}

/** A row inside a Popover used as a menu. */
export const MenuItem = ({
  onSelect,
  icon,
  shortcut,
  danger = false,
  disabled = false,
  describedBy,
  active = false,
  surface = 'paper',
  'data-tour': dataTour,
  children,
}: MenuItemProps): ReactElement => (
  <button
    type="button"
    role="menuitem"
    data-tour={dataTour}
    aria-disabled={disabled || undefined}
    aria-describedby={describedBy}
    onClick={() => {
      if (disabled) return
      onSelect()
    }}
    className={cn(
      't-small ring-focus-inset flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors',
      'aria-disabled:cursor-not-allowed aria-disabled:opacity-45',
      // No `danger` MenuItem currently renders with `surface="chrome"` (grep confirms), so this
      // stays a single paper-only value rather than guessing an untested chrome-readable red —
      // `-deep` fails AA on a dark background the same way the base token fails on paper.
      danger
        ? 'text-down-deep hover:bg-sunken'
        : surface === 'chrome'
          ? 'text-chrome-ink hover:bg-chrome-600'
          : 'text-ink hover:bg-sunken',
      disabled && 'hover:bg-transparent',
      active && (surface === 'chrome' ? 'bg-chrome-600' : 'bg-brass-wash'),
    )}
  >
    {icon ? (
      <span className={cn('shrink-0', surface === 'chrome' ? 'text-chrome-mute' : 'text-ink-faint')} aria-hidden="true">
        {icon}
      </span>
    ) : null}
    <span className="min-w-0 flex-1 truncate">{children}</span>
    {shortcut ? (
      // Real read text (the key combo), not decorative like the icon above — ink-mute, not
      // ink-faint, matching MenuNote below.
      <span className={cn('t-mono shrink-0', surface === 'chrome' ? 'text-chrome-mute' : 'text-ink-mute')}>
        {shortcut}
      </span>
    ) : null}
  </button>
)

export interface MenuSeparatorProps {
  surface?: 'paper' | 'chrome'
}

export interface MenuNoteProps {
  /** Point the disabled rows at this with `MenuItem`'s `describedBy`. */
  id?: string
  children: ReactNode
  surface?: 'paper' | 'chrome'
}

/**
 * Why the rows under it are unavailable. Said once, in full sentences, next to the commands it
 * explains — never in place of them.
 */
export const MenuNote = ({ id, children, surface = 'paper' }: MenuNoteProps): ReactElement => (
  <p
    id={id}
    role="none"
    className={cn(
      't-small px-3 pt-2 pb-1',
      surface === 'chrome' ? 'text-chrome-mute' : 'text-ink-mute',
    )}
  >
    {children}
  </p>
)

export const MenuSeparator = ({ surface = 'paper' }: MenuSeparatorProps): ReactElement => (
  <div
    role="separator"
    className={cn('my-1 h-px', surface === 'chrome' ? 'bg-chrome-600' : 'bg-hair')}
  />
)

export interface MenuLabelProps {
  children: ReactNode
  surface?: 'paper' | 'chrome'
}

export const MenuLabel = ({ children, surface = 'paper' }: MenuLabelProps): ReactElement => (
  // A real category heading ("Organizzazione", "Lingua") — ink-mute, not the decorative-only
  // ink-faint. AudiencePanel's own t-label headers already made this call correctly; this one
  // hadn't yet.
  <p
    className={cn(
      't-label px-3 pt-2.5 pb-1',
      surface === 'chrome' ? 'text-chrome-mute' : 'text-ink-mute',
    )}
  >
    {children}
  </p>
)
